// frontend/src/components/account/AddLocationPanel.tsx
import React, { useState } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  message,
  Row,
  Col,
  Alert,
  Typography,
  Modal,
  Divider,
  Spin,
  Space,
  Tag,
  Steps,
  Checkbox,
} from "antd";
import {
  HomeOutlined,
  CreditCardOutlined,
  CheckCircleOutlined,
  LoadingOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
} from "@ant-design/icons";
import { useSelector, useDispatch } from "react-redux";
import { setUser } from "../../../app.slice";
import {
  useGetProPricingQuery,
  useConfirmAddLocationMutation,
} from "../payment.services";
import { useFetchMyLocationsQuery } from "../../restaurant/restaruant.service";
import { ADDITIONAL_LOCATION_PRICE } from "../../../constant/pricing";

const { Title, Text } = Typography;
const { Step } = Steps;

const AddLocationPanel = ({ onSuccess, onCancel }) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const user = useSelector((state) => state.app.user);

  const [currentStep, setCurrentStep] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [locationData, setLocationData] = useState(null);

  const { data: pricingData, isLoading: loadingPricing, error: pricingError } =
    useGetProPricingQuery();
  const { data: locationsData, refetch: refetchLocations } =
    useFetchMyLocationsQuery();

  const [confirmAddLocation, { isLoading: confirming }] =
    useConfirmAddLocationMutation();

  const locationPrice =
    pricingData?.data?.locationPrice ?? ADDITIONAL_LOCATION_PRICE;
  const priceFetched = pricingData?.data?.priceFetched || false;
  const cardOnFile = pricingData?.data?.cardOnFile;
  const hasPaymentMethod =
    pricingData?.data?.hasPaymentMethod ||
    !!(cardOnFile?.last4 || user?.lastFourDigits);
  const cardLast4 = cardOnFile?.last4 || user?.lastFourDigits || "****";
  const cardBrand = (cardOnFile?.brand || user?.paymentMethod || "card")
    .toString()
    .replace(/^./, (c) => c.toUpperCase());

  const currentLocations =
    locationsData?.data?.totalLocations ??
    locationsData?.data?.locations?.length ??
    user?.locationIds?.length ??
    (user?.restaurantId ? 1 : 0);

  // Paid adds are allowed whenever a subscription/card exists
  const canAddLocation =
    pricingData?.data?.canAddMore !== false &&
    (hasPaymentMethod || !!pricingData?.data?.subscription);

  const onFinish = async (values) => {
    if (!hasPaymentMethod) {
      message.error(
        "No card on file. Please update your payment method in My Account first."
      );
      return;
    }

    if (pricingData && locationPrice === 0 && !priceFetched) {
      message.error("Price not available. Please try again.");
      return;
    }

    setLocationData({
      locationName: values.locationName,
      locationAddress: values.locationAddress,
      phone: values.phone || "",
      localAdminName: values.localAdminName,
      localAdminEmail: values.localAdminEmail,
      copyOptions: {
        checklists: false,
        suppliers: values.copySuppliers !== false,
        products: values.copyProducts !== false,
        settings: values.copySettings !== false,
      },
    });
    setShowConfirmModal(true);
    setCurrentStep(1);
  };

  const handleConfirm = async () => {
    if (!locationData) return;

    setIsProcessing(true);
    setCurrentStep(2);

    try {
      const result = await confirmAddLocation({
        ...locationData,
        userId: user._id,
        amount: locationPrice,
      }).unwrap();

      const locationId = result?.data?.locationId;
      if (locationId) {
        const updatedUser = {
          ...user,
          locationIds: [...(user?.locationIds || []), locationId],
          activeLocationId: locationId,
          restaurantId: locationId,
          maxLocations: Math.max(
            1,
            ((user?.locationIds?.length || 0) + 1)
          ),
          lastFourDigits: result?.data?.cardOnFile?.last4 || user?.lastFourDigits,
        };
        dispatch(setUser(updatedUser));
        localStorage.setItem("user", JSON.stringify(updatedUser));
      }

      try {
        await refetchLocations();
      } catch (_) {
        /* ignore */
      }

      setShowConfirmModal(false);
      setCurrentStep(3);
      message.success("Location added to your subscription");
    } catch (error) {
      console.error("Confirm add location error:", error);
      message.error(
        error?.data?.message ||
          error?.message ||
          "Could not add location. Please try again."
      );
      setCurrentStep(1);
    } finally {
      setIsProcessing(false);
    }
  };

  if (loadingPricing) {
    return (
      <div style={{ textAlign: "center", padding: "40px" }}>
        <Spin size="large" />
        <p style={{ marginTop: 16 }}>Loading pricing...</p>
      </div>
    );
  }

  if (pricingError) {
    return (
      <Alert
        message="Error loading pricing"
        description={pricingError.data?.message || "Please try again later"}
        type="error"
        showIcon
        action={
          <Button size="small" onClick={() => window.location.reload()}>
            Retry
          </Button>
        }
      />
    );
  }

  if (!hasPaymentMethod) {
    return (
      <Alert
        message="No card on file"
        description="Add or update your payment method in My Account first. Extra locations are charged to the card on your SFBB subscription — you won’t need to enter card details again."
        type="warning"
        showIcon
        icon={<CreditCardOutlined />}
      />
    );
  }

  if (pricingData && locationPrice === 0 && !priceFetched) {
    return (
      <Alert
        message="Price not available"
        description="Could not fetch pricing from Stripe. Please try again."
        type="error"
        showIcon
        action={
          <Button size="small" onClick={() => window.location.reload()}>
            Retry
          </Button>
        }
      />
    );
  }

  return (
    <Card
      bordered={false}
      style={{ borderRadius: "12px", boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}
    >
      <div style={{ textAlign: "left", marginBottom: 16 }}>
        <Text type="secondary">
          Add a new location to your SFBB subscription
        </Text>
      </div>

      <div
        style={{
          background: "#f3faf0",
          padding: "12px 16px",
          borderRadius: "8px",
          marginBottom: 16,
          border: "1px solid #cfe9b8",
        }}
      >
        <Row justify="space-between" align="middle" gutter={[8, 8]}>
          <Col>
            <Text strong>Current locations:</Text>
            <Tag
              style={{
                color: "#6DB33F",
                background: "#fff",
                borderColor: "#6DB33F",
                marginLeft: 8,
              }}
            >
              {currentLocations}
            </Tag>
          </Col>
          <Col>
            <Text>Extra location:</Text>
            <Tag
              style={{
                color: "#6DB33F",
                background: "#fff",
                borderColor: "#6DB33F",
                marginLeft: 8,
              }}
            >
              £{locationPrice.toFixed(2)}/month
            </Tag>
          </Col>
          <Col>
            <Text>Card on file:</Text>
            <Tag
              style={{
                color: "#6DB33F",
                background: "#fff",
                borderColor: "#6DB33F",
                marginLeft: 8,
              }}
            >
              {cardBrand} •••• {cardLast4}
            </Tag>
          </Col>
        </Row>
      </div>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
        message="How billing works"
        description={
          <div>
            <p style={{ marginBottom: 8 }}>
              You confirm once — we charge the card already on your SFBB
              subscription. <strong>No card details to enter.</strong>
            </p>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              <li>
                <strong>Today:</strong> a prorated charge for £
                {locationPrice.toFixed(2)} (or the remaining period) goes to{" "}
                {cardBrand} •••• {cardLast4}.
              </li>
              <li>
                <strong>Ongoing:</strong> £{locationPrice.toFixed(2)}/month is
                added to your existing SFBB subscription.
              </li>
              <li>
                <strong>Secure:</strong> payment stays with Stripe using your
                saved subscription card — SFBB never asks you to re-type it.
              </li>
            </ul>
          </div>
        }
      />

      <Steps current={currentStep} style={{ marginBottom: 32 }}>
        <Step title="Details" icon={<HomeOutlined />} />
        <Step title="Confirm" icon={<CreditCardOutlined />} />
        <Step title="Processing" icon={<LoadingOutlined />} />
        <Step title="Done" icon={<CheckCircleOutlined />} />
      </Steps>

      {currentStep === 0 && (
        <Form form={form} onFinish={onFinish} layout="vertical">
          <Alert
            message={`£${locationPrice.toFixed(2)}/month will be added to your subscription (card •••• ${cardLast4})`}
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item
                name="locationName"
                label="Location Name"
                rules={[
                  { required: true, message: "Please enter location name" },
                ]}
              >
                <Input
                  placeholder="e.g., Manchester Branch"
                  size="large"
                  prefix={<HomeOutlined />}
                />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                name="locationAddress"
                label="Location Address"
                rules={[{ required: true, message: "Please enter address" }]}
              >
                <Input.TextArea
                  placeholder="Full address (street, city, postcode)"
                  rows={3}
                  size="large"
                />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="phone" label="Location Phone (optional)">
                <Input
                  placeholder="e.g., 020 1234 5678"
                  size="large"
                  prefix={<PhoneOutlined />}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Copy from existing location</Divider>
          <Alert
            message="Optional — copy setup into the new site"
            description="Suppliers, label products and due-time settings can be copied. If you leave “Copy label products / categories” unchecked, starter categories (Dairy, Meat, Vegetables) with example products are added instead. Checklists are set up via the Location Administrator’s setup wizard. Completed records, reports, staff, deliveries and temperatures are never copied."
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
          />
          <Form.Item
            name="copySuppliers"
            valuePropName="checked"
            initialValue={true}
          >
            <Checkbox>Copy suppliers</Checkbox>
          </Form.Item>
          <Form.Item
            name="copyProducts"
            valuePropName="checked"
            initialValue={true}
          >
            <Checkbox>
              Copy label products / categories from an existing location (if
              unchecked, starter categories are added)
            </Checkbox>
          </Form.Item>
          <Form.Item
            name="copySettings"
            valuePropName="checked"
            initialValue={true}
          >
            <Checkbox>
              Copy due times, opening hours &amp; alert settings
            </Checkbox>
          </Form.Item>

          <Divider orientation="left">Location Administrator</Divider>
          <Alert
            message="Assign a Location Administrator"
            description="This person will manage this location's food safety records, staff, and checklists."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item
                name="localAdminName"
                label="Administrator Name"
                rules={[
                  {
                    required: true,
                    message: "Please enter administrator name",
                  },
                ]}
              >
                <Input
                  placeholder="e.g., John Smith"
                  size="large"
                  prefix={<UserOutlined />}
                />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                name="localAdminEmail"
                label="Administrator Email"
                rules={[
                  {
                    required: true,
                    type: "email",
                    message: "Please enter valid email",
                  },
                ]}
              >
                <Input
                  placeholder="john@restaurant.com"
                  size="large"
                  prefix={<MailOutlined />}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          <Space style={{ width: "100%", justifyContent: "space-between" }}>
            {onCancel && (
              <Button size="large" onClick={onCancel}>
                Cancel
              </Button>
            )}
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              disabled={!canAddLocation}
              style={{
                background: "#6DB33F",
                borderColor: "#6DB33F",
                borderRadius: "8px",
                padding: "0 40px",
              }}
            >
              Continue to confirm
            </Button>
          </Space>
        </Form>
      )}

      <Modal
        title={
          <div style={{ textAlign: "center" }}>
            <CreditCardOutlined style={{ fontSize: 24, color: "#6DB33F" }} />
            <span style={{ marginLeft: 8 }}>Confirm add location</span>
          </div>
        }
        open={showConfirmModal}
        onCancel={() => {
          if (!isProcessing && !confirming) {
            setShowConfirmModal(false);
            setCurrentStep(0);
          }
        }}
        footer={null}
        width={520}
        centered
        closable={!isProcessing && !confirming}
        maskClosable={!isProcessing && !confirming}
      >
        <div style={{ padding: "8px 0" }}>
          {isProcessing || confirming ? (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <Spin size="large" />
              <Title level={4} style={{ marginTop: 24 }}>
                Updating your subscription...
              </Title>
              <Text type="secondary">
                Charging {cardBrand} •••• {cardLast4} and creating the location.
              </Text>
            </div>
          ) : (
            <>
              <div
                style={{
                  background: "#f3faf0",
                  padding: 16,
                  borderRadius: 8,
                  border: "1px solid #6DB33F",
                  marginBottom: 16,
                  textAlign: "center",
                }}
              >
                <Text strong style={{ display: "block", fontSize: 18 }}>
                  {locationData?.locationName || "New Location"}
                </Text>
                <Text type="secondary">
                  £{locationPrice.toFixed(2)}/month on your SFBB subscription
                </Text>
              </div>

              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="No card entry needed"
                description={
                  <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
                    <li>
                      Charge goes to <strong>{cardBrand} •••• {cardLast4}</strong>{" "}
                      already on your subscription.
                    </li>
                    <li>
                      £{locationPrice.toFixed(2)}/month is added to your plan
                      for this location.
                    </li>
                    <li>You don’t need a Stripe login or to re-enter a card.</li>
                  </ul>
                }
              />

              <Button
                type="primary"
                size="large"
                block
                onClick={handleConfirm}
                style={{
                  background: "#6DB33F",
                  borderColor: "#6DB33F",
                  height: 48,
                  borderRadius: 8,
                }}
              >
                Confirm &amp; add location — £{locationPrice.toFixed(2)}/month
              </Button>
              <Button
                size="large"
                block
                style={{ marginTop: 8 }}
                onClick={() => {
                  setShowConfirmModal(false);
                  setCurrentStep(0);
                }}
              >
                Back
              </Button>
            </>
          )}
        </div>
      </Modal>

      {currentStep === 3 && (
        <div style={{ textAlign: "center", padding: "32px 0" }}>
          <CheckCircleOutlined style={{ fontSize: 64, color: "#6DB33F" }} />
          <Title level={3} style={{ marginTop: 16 }}>
            Location Added Successfully!
          </Title>
          <div
            style={{
              background: "#f0f7ff",
              padding: "16px 20px",
              borderRadius: "8px",
              margin: "16px 0",
              border: "1px solid #d6e4ff",
              textAlign: "left",
            }}
          >
            <Text strong style={{ display: "block", fontSize: "15px" }}>
              Subscription updated
            </Text>
            <Text
              type="secondary"
              style={{ display: "block", marginTop: 8, fontSize: 13 }}
            >
              £{locationPrice.toFixed(2)}/month has been added to your plan and
              charged to {cardBrand} •••• {cardLast4}.
            </Text>
            <Text
              strong
              style={{ display: "block", marginTop: 12, fontSize: "15px" }}
            >
              Invitation sent to administrator
            </Text>
            <Text
              type="secondary"
              style={{ display: "block", marginTop: 8, fontSize: 13 }}
            >
              An email was sent to{" "}
              <strong>{locationData?.localAdminEmail}</strong> to set their
              password and access this location.
            </Text>
          </div>
          <div style={{ marginTop: 24 }}>
            <Button
              type="primary"
              size="large"
              onClick={onSuccess}
              style={{
                background: "#6DB33F",
                borderColor: "#6DB33F",
                borderRadius: "8px",
              }}
            >
              <HomeOutlined /> Go to Dashboard
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
};

export default AddLocationPanel;
